// internal/WorkspacePage.jsx — Workspace collaborateur fidèle aux maquettes (sidebar noire brandée + zone claire + assistant AYA + objectifs).
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/hooks/useAuth";
import { Bell, Search, Send, ChevronLeft } from "lucide-react";
import { S as DS, IT } from "@/internal/InternalLayout";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api/internal`;
const H = () => ({ "Content-Type": "application/json", Authorization: `Bearer ${localStorage.getItem("civ_user_token") || ""}` });
const GREEN = "#12B76A";
const ST = {
  todo: { l: "À faire", c: "#64748B", bg: "#F1F5F9" }, doing: { l: "En cours", c: "#0284C7", bg: "#E0F2FE" },
  blocked: { l: "Bloqué", c: "#DC2626", bg: "#FEE2E2" }, review: { l: "En validation", c: "#B45309", bg: "#FEF3C7" },
  done: { l: "Terminé", c: "#15803D", bg: "#DCFCE7" },
};
const NEXT = { todo: "doing", doing: "review", blocked: "doing", review: "done" };
const PRIO = { haute: "#DC2626", normale: "#B45309", basse: "#64748B" };

function Copilot({ slug, copilotName, color }) {
  const [msgs, setMsgs] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const boxRef = useRef(null);
  useEffect(() => { boxRef.current?.scrollTo(0, boxRef.current.scrollHeight); }, [msgs, busy]);

  const send = async (text) => {
    const message = (text || input).trim();
    if (!message || busy) return;
    setInput("");
    const hist = msgs;
    setMsgs((m) => [...m, { role: "user", content: message }]);
    setBusy(true);
    try {
      const r = await fetch(`${API}/workspaces/${slug}/copilot`, { method: "POST", headers: H(), body: JSON.stringify({ message, history: hist }) });
      const d = await r.json();
      if (!d.ok) throw new Error(d.message || d.error);
      setMsgs((m) => [...m, { role: "assistant", content: d.reply }]);
    } catch {
      setMsgs((m) => [...m, { role: "assistant", content: "⚠️ Copilot momentanément indisponible." }]);
    } finally { setBusy(false); }
  };

  return (
    <div style={{ background: "#fff", border: "1px solid #E5E7EB", borderRadius: 16, display: "flex", flexDirection: "column", minHeight: 420, maxHeight: 560, overflow: "hidden" }} data-testid="ws-copilot">
      <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "13px 15px", borderBottom: "1px solid #F1F5F9" }}>
        <span style={{ width: 34, height: 34, borderRadius: 10, background: "#06281A", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 15 }}>🤖</span>
        <span>
          <span style={{ display: "block", fontSize: 13.5, fontWeight: 900, color: "#0B1611" }}>AYA</span>
          <span style={{ fontSize: 10.5, color: "#6B7280", fontWeight: 600 }}>{copilotName} · Assistant IA</span>
        </span>
        <span style={{ marginLeft: "auto", width: 8, height: 8, borderRadius: 99, background: GREEN }} />
      </div>
      <div ref={boxRef} style={{ flex: 1, overflowY: "auto", padding: 14, display: "flex", flexDirection: "column", gap: 9 }}>
        {msgs.length === 0 && (
          <div style={{ fontSize: 12, color: "#6B7280", lineHeight: 1.6 }}>
            Je connais vos KPI, objectifs, tâches et responsabilités en temps réel.
            <div style={{ display: "flex", flexDirection: "column", gap: 6, marginTop: 10 }}>
              {["Où en suis-je sur mes objectifs ?", "Quelles sont mes priorités aujourd'hui ?", "Quelles tâches sont bloquées ?"].map((q) => (
                <button key={q} onClick={() => send(q)} data-testid="copilot-suggestion"
                  style={{ textAlign: "left", padding: "8px 11px", borderRadius: 10, border: "1px solid #E5E7EB", background: "#F9FAFB", color: "#374151", fontSize: 11.5, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>
                  💡 {q}
                </button>
              ))}
            </div>
          </div>
        )}
        {msgs.map((m, i) => (
          <div key={i} style={{
            alignSelf: m.role === "user" ? "flex-end" : "flex-start", maxWidth: "90%", padding: "9px 12px", borderRadius: 13, fontSize: 12.5, lineHeight: 1.55, whiteSpace: "pre-wrap",
            background: m.role === "user" ? "#EBF5FF" : "#F3F8F4", color: "#1F2937",
            border: m.role === "user" ? "1px solid #D4E8FC" : "1px solid #DCEEE0",
          }}>{m.content}</div>
        ))}
        {busy && <div style={{ fontSize: 11.5, color: "#6B7280" }}>⏳ AYA réfléchit…</div>}
      </div>
      <div style={{ display: "flex", gap: 7, padding: 12, borderTop: "1px solid #F1F5F9" }}>
        <input data-testid="copilot-input" value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()}
          placeholder="Écrivez à AYA…"
          style={{ flex: 1, background: "#F9FAFB", border: "1px solid #E5E7EB", borderRadius: 999, padding: "9px 14px", color: "#111827", fontSize: 12.5, outline: "none", fontFamily: "inherit" }} />
        <button data-testid="copilot-send" onClick={() => send()} disabled={busy || !input.trim()}
          style={{ width: 38, height: 38, borderRadius: 99, border: "none", background: color || GREEN, color: "#fff", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
          <Send size={15} />
        </button>
      </div>
    </div>
  );
}

function Handover({ slug, ownerName, onDone }) {
  const [open, setOpen] = useState(false);
  const [h, setH] = useState(null);
  const [busy, setBusy] = useState(false);
  const prepare = async () => {
    setOpen(true);
    const r = await fetch(`${API}/workspaces/${slug}/handover`, { headers: H() });
    const d = await r.json();
    if (d.ok) setH(d.handover);
  };
  const transfer = async () => {
    setBusy(true);
    try {
      const r = await fetch(`${API}/workspaces/${slug}/handover/transfer`, { method: "POST", headers: H() });
      const d = await r.json();
      if (!d.ok) throw new Error(d.error);
      toast.success(`✅ ${d.transferred} responsabilité(s) transférée(s) vers votre workspace`);
      setOpen(false); onDone();
    } catch (e) { toast.error(String(e.message || e)); }
    finally { setBusy(false); }
  };
  return (
    <>
      <button data-testid="ws-handover-btn" onClick={prepare}
        style={{ padding: "7px 13px", borderRadius: 10, border: "1px solid rgba(180,83,9,0.35)", background: "#FEF3C7", color: "#92400E", fontSize: 11.5, fontWeight: 800, cursor: "pointer", fontFamily: "inherit" }}>
        🛟 Préparer le handover
      </button>
      {open && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)", zIndex: 60, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }} onClick={() => setOpen(false)}>
          <div onClick={(e) => e.stopPropagation()} style={{ background: "#fff", border: "1px solid #E5E7EB", borderRadius: 16, padding: 20, maxWidth: 480, width: "100%", maxHeight: "82vh", overflowY: "auto", color: "#111827" }} data-testid="handover-modal">
            <h2 style={{ fontSize: 17, fontWeight: 900, margin: 0 }}>🛟 Handover — {ownerName}</h2>
            <div style={{ fontSize: 12, color: "#6B7280", margin: "4px 0 12px" }}>Transfert de toutes les responsabilités et tâches ouvertes vers votre workspace CEO.</div>
            {!h && <div style={{ color: "#6B7280", fontSize: 12.5 }}>Préparation…</div>}
            {h && (
              <div style={{ fontSize: 12.5, display: "flex", flexDirection: "column", gap: 10 }}>
                <div><strong style={{ color: GREEN }}>Responsabilités ({h.responsibilities.length})</strong>{h.responsibilities.map((r, i) => <div key={i} style={{ color: "#4B5563", padding: "3px 0" }}>• {r.label} {!r.backup && <span style={{ color: "#DC2626" }}>(sans backup)</span>}</div>)}</div>
                <div><strong style={{ color: GREEN }}>Tâches ouvertes ({h.open_tasks.length})</strong>{h.open_tasks.map((t, i) => <div key={i} style={{ color: "#4B5563", padding: "3px 0" }}>• {t.title} <span style={{ color: (ST[t.status] || {}).c }}>[{(ST[t.status] || {}).l || t.status}]</span></div>)}</div>
                {h.risks.length > 0 && <div style={{ color: "#DC2626" }}><strong>⚠ Risques</strong>{h.risks.map((r, i) => <div key={i} style={{ padding: "3px 0" }}>• {r}</div>)}</div>}
              </div>
            )}
            <div style={{ display: "flex", gap: 8, marginTop: 16 }}>
              <button onClick={() => setOpen(false)} style={{ padding: "9px 14px", borderRadius: 10, border: "1px solid #E5E7EB", background: "#fff", color: "#6B7280", fontSize: 12.5, fontWeight: 800, cursor: "pointer", fontFamily: "inherit" }}>Annuler</button>
              <button data-testid="handover-transfer" onClick={transfer} disabled={busy || !h}
                style={{ padding: "9px 16px", borderRadius: 10, border: "none", background: GREEN, color: "#fff", fontSize: 12.5, fontWeight: 800, cursor: "pointer", fontFamily: "inherit" }}>✓ Transférer vers mon workspace</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default function WorkspacePage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const [d, setD] = useState(null);
  const [err, setErr] = useState(null);
  const [newTask, setNewTask] = useState("");
  const [activeModule, setActiveModule] = useState(null);

  useEffect(() => { if (!isAuthenticated) navigate(`/login?next=/internal/ws/${slug}`); }, [isAuthenticated, navigate, slug]);
  // Studios dédiés : tech → AYA Tech Studio, product → AYA Product Studio.
  useEffect(() => {
    if (slug === "tech") navigate("/internal/tech-studio", { replace: true });
    else if (slug === "product") navigate("/internal/product-studio", { replace: true });
  }, [slug, navigate]);

  const load = useCallback(() => {
    fetch(`${API}/workspaces/${slug}`, { headers: H() })
      .then((r) => r.json()).then((x) => (x.ok ? setD(x) : setErr(x.error))).catch(() => setErr("network"));
  }, [slug]);
  useEffect(() => { load(); }, [load]);

  const addTask = async () => {
    const title = newTask.trim();
    if (!title) return;
    const r = await fetch(`${API}/workspaces/${slug}/tasks`, { method: "POST", headers: H(), body: JSON.stringify({ title }) });
    const x = await r.json();
    if (x.ok) { setNewTask(""); toast.success("Tâche créée"); load(); } else toast.error(x.error);
  };
  const advance = async (t) => {
    const status = NEXT[t.status];
    if (!status) return;
    await fetch(`${API}/tasks/${t.task_id}`, { method: "PATCH", headers: H(), body: JSON.stringify({ status }) });
    load();
  };

  if (err) return (
    <div style={{ minHeight: "100vh", background: IT.bg, color: IT.text, display: "flex", alignItems: "center", justifyContent: "center", flexDirection: "column", gap: 10 }} data-testid="ws-error">
      <div style={{ fontWeight: 800 }}>Accès refusé à ce workspace ({err}).</div>
      <button style={DS.ghost} onClick={() => navigate("/search")}>Retour à AYA</button>
    </div>
  );
  if (!d) return <div style={{ minHeight: "100vh", background: "#F6F8F7", display: "flex", alignItems: "center", justifyContent: "center", color: "#6B7280", fontSize: 13 }}>Chargement…</div>;

  const w = d.workspace;
  const nameParts = w.name.replace(/^AYA\s*/, "").trim();
  const goal = d.goals[0];
  const goalPct = goal ? Math.min(100, Math.round((goal.current / goal.target) * 100)) : null;
  const displayName = d.mode_ceo ? "Cédric" : (user?.display_name || w.owner_name);
  const displayRole = d.mode_ceo ? "CEO · Founder" : w.role_title;

  return (
    <div style={{ minHeight: "100vh", background: "#F6F8F7", display: "flex", fontFamily: "inherit" }} data-testid="ws-shell">
      {/* Sidebar noire brandée (maquette Tech/Product Studio) */}
      <aside className="aya-int-sidebar" style={{ width: 216, flexShrink: 0, background: "#0A0F0C", padding: "18px 12px", position: "sticky", top: 0, height: "100vh", overflowY: "auto", scrollbarWidth: "none", display: "flex", flexDirection: "column" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 9, padding: "0 6px 16px" }}>
          <span style={{ width: 36, height: 36, borderRadius: 11, background: `${w.color}22`, border: `1px solid ${w.color}66`, display: "flex", alignItems: "center", justifyContent: "center" }}>
            <img src="/aya/splash.png" alt="AYA" style={{ width: 24, height: 24, objectFit: "contain" }} />
          </span>
          <span>
            <span style={{ display: "block", fontSize: 16, fontWeight: 900, color: "#fff", letterSpacing: 1 }}>AYA</span>
            <span style={{ fontSize: 9.5, color: w.color, fontWeight: 800 }}>{nameParts}</span>
          </span>
        </div>
        <button onClick={() => setActiveModule(null)} data-testid="ws-nav-workspace"
          style={{ display: "flex", alignItems: "center", gap: 9, padding: "10px 11px", marginBottom: 3, borderRadius: 10, fontSize: 12.5, fontWeight: 800, border: "none", cursor: "pointer", fontFamily: "inherit", textAlign: "left",
            background: activeModule == null ? GREEN : "transparent", color: activeModule == null ? "#04120B" : "#8BA394" }}>
          🏠 Espace de travail
        </button>
        {(w.modules || []).filter((m) => m.key !== "board").map((m) => (
          <button key={m.key} data-testid={`ws-nav-${m.key}`}
            onClick={() => (m.link ? navigate(m.link) : setActiveModule(m.key))}
            style={{ display: "flex", alignItems: "center", gap: 9, padding: "10px 11px", marginBottom: 3, borderRadius: 10, fontSize: 12.5, fontWeight: 700, border: "none", cursor: "pointer", fontFamily: "inherit", textAlign: "left",
              background: activeModule === m.key ? "rgba(46,226,123,0.14)" : "transparent",
              color: activeModule === m.key ? "#2EE27B" : "#8BA394" }}>
            {m.icon} {m.label}{m.link ? " ↗" : ""}
          </button>
        ))}
        <div style={{ marginTop: "auto", paddingTop: 14 }}>
          {d.mode_ceo && (
            <button onClick={() => navigate("/internal")} data-testid="ws-back-cc"
              style={{ display: "flex", alignItems: "center", gap: 6, width: "100%", justifyContent: "center", padding: "9px 10px", borderRadius: 10, border: "1px solid rgba(232,184,75,0.4)", background: "rgba(232,184,75,0.08)", color: "#E8B84B", fontSize: 11.5, fontWeight: 800, cursor: "pointer", fontFamily: "inherit", marginBottom: 8, boxSizing: "border-box" }}>
              <ChevronLeft size={13} /> Command Center
            </button>
          )}
          <div style={{ display: "flex", alignItems: "center", gap: 9, padding: "10px 8px", borderTop: "1px solid rgba(255,255,255,0.08)" }}>
            <span style={{ width: 32, height: 32, borderRadius: 99, background: `${w.color}33`, color: w.color, fontWeight: 900, fontSize: 13, display: "flex", alignItems: "center", justifyContent: "center" }}>{displayName[0]}</span>
            <span style={{ minWidth: 0 }}>
              <span style={{ display: "block", fontSize: 12, fontWeight: 800, color: "#fff" }}>{displayName}</span>
              <span style={{ fontSize: 9.5, color: "#8BA394" }}>{displayRole}</span>
            </span>
          </div>
        </div>
      </aside>

      {/* Zone de travail claire */}
      <main style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 22px", background: "#fff", borderBottom: "1px solid #E5E7EB" }}>
          <div style={{ flex: 1, display: "flex", alignItems: "center", gap: 8, maxWidth: 560, background: "#F3F4F6", borderRadius: 999, padding: "8px 14px", color: "#9CA3AF", fontSize: 12.5 }}>
            <Search size={14} /> Rechercher une tâche, un objectif, une commande…
          </div>
          <Bell size={17} color="#6B7280" />
          <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span style={{ width: 30, height: 30, borderRadius: 99, background: `${w.color}22`, color: w.color, fontWeight: 900, fontSize: 12, display: "flex", alignItems: "center", justifyContent: "center" }}>{displayName[0]}</span>
            <span style={{ fontSize: 12, fontWeight: 800, color: "#111827" }}>{displayName}<span style={{ display: "block", fontSize: 9.5, color: "#6B7280", fontWeight: 600 }}>{displayRole}</span></span>
          </span>
        </div>

        <div style={{ padding: "20px 22px 44px" }}>
          {d.mode_ceo && (
            <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 14px", borderRadius: 12, marginBottom: 16, background: "#FFFBEB", border: "1px solid #FDE68A", flexWrap: "wrap" }} data-testid="ws-mode-ceo">
              <span style={{ fontWeight: 900, color: "#92400E", fontSize: 13 }}>🔐 MODE CEO</span>
              <span style={{ fontSize: 12, color: "#92400E", flex: 1, minWidth: 180 }}>Vous travaillez dans l'espace de {w.owner_name} avec vos permissions CEO.</span>
              <Handover slug={slug} ownerName={w.owner_name} onDone={load} />
            </div>
          )}

          <h1 style={{ fontSize: 30, fontWeight: 900, margin: 0, color: "#0B1611" }}>
            AYA <span style={{ color: GREEN }}>{nameParts}</span>
          </h1>
          <div style={{ fontSize: 13, color: "#6B7280", marginTop: 4, marginBottom: 18 }}>
            {w.owner_name} · {w.role_title} · Copilot <strong style={{ color: GREEN }}>{w.copilot}</strong>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))", gap: 14, alignItems: "start" }}>
            {/* Colonne gauche : assistant AYA (maquette) */}
            <Copilot slug={slug} copilotName={w.copilot} color={GREEN} />

            {/* Colonne centrale : KPI + tâches */}
            <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(125px, 1fr))", gap: 10 }} data-testid="ws-kpis">
                {(w.kpis || []).map((k) => (
                  <div key={k.key} style={{ background: "#fff", border: "1px solid #E5E7EB", borderRadius: 14, padding: "12px 13px" }}>
                    <div style={{ fontSize: 9.5, fontWeight: 800, color: "#6B7280", textTransform: "uppercase", letterSpacing: 0.4 }}>{k.label}</div>
                    <div style={{ fontSize: 21, fontWeight: 900, marginTop: 4, color: "#0B1611" }}>{k.value ?? 0}{k.unit}</div>
                    <div style={{ fontSize: 10, color: "#9CA3AF" }}>Cible : {k.target}{k.unit}</div>
                  </div>
                ))}
              </div>

              <div style={{ background: "#fff", border: "1px solid #E5E7EB", borderRadius: 16, padding: 16 }} data-testid="ws-tasks">
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
                  <h2 style={{ fontSize: 14.5, fontWeight: 900, margin: 0, color: "#0B1611" }}>📋 Backlog & tâches</h2>
                  <span style={{ fontSize: 10.5, color: "#6B7280", fontWeight: 700 }}>{d.tasks.filter((t) => t.status !== "done").length} ouvertes</span>
                </div>
                <div style={{ display: "flex", gap: 7, marginBottom: 10 }}>
                  <input data-testid="task-input" value={newTask} onChange={(e) => setNewTask(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addTask()}
                    placeholder="Nouvelle tâche…"
                    style={{ flex: 1, background: "#F9FAFB", border: "1px solid #E5E7EB", borderRadius: 10, padding: "8px 12px", color: "#111827", fontSize: 12.5, outline: "none", fontFamily: "inherit" }} />
                  <button data-testid="task-add" onClick={addTask}
                    style={{ padding: "8px 14px", borderRadius: 10, border: "none", background: GREEN, color: "#fff", fontSize: 12, fontWeight: 800, cursor: "pointer", fontFamily: "inherit" }}>+ Ajouter</button>
                </div>
                {d.tasks.length === 0 && <div style={{ fontSize: 12, color: "#6B7280" }}>Aucune tâche.</div>}
                {d.tasks.map((t) => {
                  const s = ST[t.status] || { l: t.status, c: "#64748B", bg: "#F1F5F9" };
                  return (
                    <div key={t.task_id} data-testid={`task-${t.task_id}`} style={{ display: "flex", alignItems: "center", gap: 9, padding: "9px 0", borderBottom: "1px solid #F3F4F6", opacity: t.status === "done" ? 0.55 : 1 }}>
                      <span style={{ width: 7, height: 7, borderRadius: 99, background: PRIO[t.priority] || "#9CA3AF", flexShrink: 0 }} title={`Priorité ${t.priority}`} />
                      <span style={{ flex: 1, minWidth: 0, fontSize: 12.5, fontWeight: 700, color: "#111827" }}>
                        {t.title}
                        <span style={{ display: "block", fontSize: 10, color: "#9CA3AF", fontWeight: 600 }}>{t.owner} · priorité {t.priority}</span>
                      </span>
                      <span style={{ fontSize: 9.5, fontWeight: 900, color: s.c, background: s.bg, borderRadius: 999, padding: "3px 9px", whiteSpace: "nowrap" }}>{s.l}</span>
                      {NEXT[t.status] && (
                        <button data-testid={`task-advance-${t.task_id}`} onClick={() => advance(t)} title={`→ ${ST[NEXT[t.status]].l}`}
                          style={{ padding: "4px 9px", borderRadius: 8, border: "1px solid #E5E7EB", background: "#fff", color: GREEN, fontSize: 11.5, fontWeight: 900, cursor: "pointer", fontFamily: "inherit" }}>→</button>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Colonne droite : objectif de la semaine + responsabilités (maquette Product Studio) */}
            <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
              <div style={{ background: "#fff", border: "1px solid #E5E7EB", borderRadius: 16, padding: 16 }} data-testid="ws-goals">
                <h2 style={{ fontSize: 14.5, fontWeight: 900, margin: "0 0 12px", color: "#0B1611" }}>🎯 Objectif de la période</h2>
                {goalPct != null && (
                  <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 12 }}>
                    <div style={{ width: 72, height: 72, borderRadius: 99, flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center",
                      background: `conic-gradient(${goalPct >= 80 ? GREEN : goalPct >= 50 ? "#F59E0B" : "#DC2626"} ${goalPct * 3.6}deg, #F1F5F9 0deg)` }}>
                      <span style={{ width: 54, height: 54, borderRadius: 99, background: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 900, fontSize: 15, color: "#0B1611" }} data-testid="goal-pct">{goalPct}%</span>
                    </div>
                    <div style={{ fontSize: 12.5, color: "#374151", fontWeight: 700 }}>{goal.label}<span style={{ display: "block", fontSize: 11, color: "#9CA3AF", fontWeight: 600, marginTop: 2 }}>{goal.current}{goal.unit} / {goal.target}{goal.unit}</span></div>
                  </div>
                )}
                {d.goals.slice(1).map((g, i) => {
                  const pct = Math.min(100, Math.round((g.current / g.target) * 100));
                  return (
                    <div key={i} style={{ marginBottom: 10 }}>
                      <div style={{ fontSize: 11.5, display: "flex", justifyContent: "space-between", color: "#374151" }}><span>{g.label}</span><strong style={{ color: pct >= 80 ? GREEN : "#B45309" }}>{pct}%</strong></div>
                      <div style={{ height: 6, borderRadius: 99, background: "#F1F5F9", marginTop: 5 }}>
                        <div style={{ width: `${pct}%`, height: "100%", borderRadius: 99, background: pct >= 80 ? GREEN : pct >= 50 ? "#F59E0B" : "#DC2626" }} />
                      </div>
                    </div>
                  );
                })}
                {d.goals.length === 0 && <div style={{ fontSize: 12, color: "#6B7280" }}>Aucun objectif défini.</div>}
              </div>

              {d.responsibilities.length > 0 && (
                <div style={{ background: "#fff", border: "1px solid #E5E7EB", borderRadius: 16, padding: 16 }} data-testid="ws-resp">
                  <h2 style={{ fontSize: 14.5, fontWeight: 900, margin: "0 0 10px", color: "#0B1611" }}>🛡 Responsabilités</h2>
                  {d.responsibilities.map((r) => (
                    <div key={r.key} style={{ fontSize: 12, padding: "7px 0", borderBottom: "1px solid #F3F4F6" }}>
                      <strong style={{ color: "#111827" }}>{r.label}</strong>
                      <div style={{ color: "#6B7280", marginTop: 2, fontSize: 11 }}>
                        Resp. {r.owner} · Backup <span style={{ color: r.backup ? "#374151" : "#DC2626", fontWeight: 700 }}>{r.backup || "AUCUN"}</span> · Agent <span style={{ color: GREEN, fontWeight: 700 }}>{r.agent}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
